import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, RouterLink, RouterLinkActive } from '@angular/router';

import { merge, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, share } from 'rxjs/operators';

import { AppleDeviceProfileResolverActions, selectProfileItemsByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  MasterComponent,
  MasterDetailComponent,
  PanelComponent,
  PanelFooterComponent,
  SkSpinner3Component,
} from '@rocksoft/cdk';
import { ConfigurationProfile } from '@rocksoft/mdm-sdk-v2';
import { ToastService } from '@rocksoft/shared/toast';

import { AppleCommandProgressService } from '../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../utilities/command-spinner/command-spinner.component';
import { RequestType } from '../../viewmodels/apple/commands/apple-command.model';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';
import { InstallProfileModalComponent } from './install-profile-modal/install-profile-modal.component';

@Component({
  selector: 'ml-mdm-apple-profiles',
  templateUrl: './apple-profiles.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MasterDetailComponent,
    MasterComponent,
    PanelComponent,
    SkSpinner3Component,
    RouterLinkActive,
    RouterLink,
    PanelFooterComponent,
    FaIconComponent,
    CommandSpinnerComponent,
    AsyncPipe,
  ],
})
export class AppleProfilesComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);

  udid: Observable<string> | undefined;

  private _refreshProfilesProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  public refreshProfilesProgress: Observable<CommandProgress>;

  public profiles = this.store.selectSignal(selectProfileItemsByType('configuration-profile'));

  private _profile = new Subject<ConfigurationProfile>();
  public profile: Observable<ConfigurationProfile> = this._profile.asObservable();

  private readonly subscriptions: Subscription[] = [];

  ngOnInit() {
    this.udid = this.route.parent?.params.pipe(
      map((p: Params) => p.udid),
      filter((udid) => udid),
      distinctUntilChanged()
    );

    if (!this.udid) {
      return;
    }
    this.subscriptions.push(
      this.udid.subscribe((udid) => {
        this.onUdidChanges(udid);
      })
    );
  }

  onUdidChanges(udid: string) {
    this.refreshProfilesProgress = merge(
      this._refreshProfilesProgress,
      this.commandProgressService.commandsOfRequestType(udid, 'ProfileList')
    ).pipe(share());

    this.refreshProfilesProgress
      .pipe(filter((cp: CommandProgress) => cp.status === 'Acknowledged'))
      .subscribe((cp: CommandProgress) => {
        this.store.dispatch(
          AppleDeviceProfileResolverActions.profileRequested({
            udid: cp.udid,
            profileType: 'configuration-profile',
          })
        );
      });
  }

  public refresh(udid: string): void {
    this._refreshProfilesProgress.next(this.createInitialCommandProgress(udid, 'ProfileList'));

    this.subscriptions.push(this.mdmService.getProfiles(udid).subscribe());
  }

  public showInstallProfileModal() {
    const modal = this.modalService.open(InstallProfileModalComponent, { scrollable: true, size: 'xl' });
    modal.componentInstance.udid$ = this.udid;
  }

  private createInitialCommandProgress(udid: string, requestType: RequestType): CommandProgress {
    return {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType,
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
